/**
 * lib/api.ts — Supabase CRUD katmanı
 * Tüm veritabanı işlemleri buradan yapılır.
 */
import { supabase } from './supabase';
import type { Question, QuestionDraft } from '../types/question';
import type { Test, TestDraft, TestResult, TestAnswer } from '../types/test';
import { DEFAULT_TEST_SETTINGS } from '../types/test';
import type { Book, BookDraft } from '../types/book';

// ─────────────────────────────────────────────────────────
// QUESTIONS
// ─────────────────────────────────────────────────────────

export interface QuestionFilters {
  lesson?: string;
  topic?: string;
  gradeLevel?: string;
  examType?: string;
  type?: string;
  status?: string;
  search?: string;
  isPublic?: boolean;
  viewMode?: 'my' | 'all';
  tags?: string[];
  pageSize?: number;
}

/** Öğretmenin veya yöneticinin sorularını listele (Soft delete korumalı) */
export async function fetchMyQuestions(
  authorId: string,
  filters: QuestionFilters = {},
  page = 0,
  pageSize = 30
): Promise<{ data: Question[]; count: number }> {
  const actualPageSize = filters.pageSize || pageSize;
  let query = supabase
    .from('questions')
    .select('*', { count: 'exact' })
    .neq('is_deleted', true)
    .order('created_at', { ascending: false })
    .range(page * actualPageSize, (page + 1) * actualPageSize - 1);

  if (filters.viewMode !== 'all') {
    query = query.eq('author_id', authorId);
  }

  if (filters.lesson) query = query.eq('lesson', filters.lesson);
  if (filters.topic) query = query.eq('topic', filters.topic);
  if (filters.gradeLevel) query = query.eq('grade_level', filters.gradeLevel);
  if (filters.examType) query = query.eq('exam_type', filters.examType);
  if (filters.type) query = query.eq('type', filters.type);
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.isPublic !== undefined) query = query.eq('is_public', filters.isPublic);
  if (filters.search) query = query.ilike('content', `%${filters.search}%`);
  if (filters.tags?.length) query = query.overlaps('tags', filters.tags);

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: (data ?? []) as Question[], count: count ?? 0 };
}

/** Tekil soru getir */
export async function fetchQuestion(id: string): Promise<Question> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as Question;
}

/** Yeni soru oluştur */
export async function createQuestion(
  draft: QuestionDraft,
  authorId: string
): Promise<Question> {
  const { data, error } = await supabase
    .from('questions')
    .insert({ ...draft, author_id: authorId, is_public: draft.is_public ?? false })
    .select()
    .single();
  if (error) throw error;
  return data as Question;
}

/** Soru güncelle */
export async function updateQuestion(
  id: string,
  updates: Partial<QuestionDraft>
): Promise<Question> {
  const { data, error } = await supabase
    .from('questions')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Question;
}

/** Soru sil (Soft delete — DB'de saklanır, kullanıcıya bir daha gösterilmez) */
export async function deleteQuestion(id: string): Promise<void> {
  const { error } = await supabase
    .from('questions')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Toplu soru sil (Soft delete) */
export async function deleteQuestions(ids: string[]): Promise<void> {
  const { error } = await supabase
    .from('questions')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .in('id', ids);
  if (error) throw error;
}

/** ID listesine göre sorular getir (test oluşturma için) */
export async function fetchQuestionsByIds(ids: string[]): Promise<Question[]> {
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .in('id', ids);
  if (error) throw error;
  // Sırayı koru (question_ids sırası)
  const map = new Map((data ?? []).map((q: Question) => [q.id, q]));
  return ids.map((id) => map.get(id)).filter(Boolean) as Question[];
}

/**
 * İstemci Tarafı Görsel Sıkıştırma (WebP Optimizasyonu)
 * Supabase Storage kotasını %90 tasarruflu kullanmak için görselleri maks 1200px ve %80 kaliteye çeker.
 */
export async function compressImage(file: File, maxWidth = 1200, quality = 0.82): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(file);
          }
        },
        'image/webp',
        quality
      );
    };
    img.onerror = () => resolve(file);
  });
}

/**
 * Görsel yükleme (Supabase Storage + WebP Sıkıştırma + Akıllı Fallback)
 */
export async function uploadQuestionImage(
  file: File,
  authorId: string
): Promise<string> {
  try {
    // 1. İstemci tarafında WebP formatında sıkıştır (%90 Kota Tasarrufu)
    const compressedBlob = await compressImage(file);
    const path = `${authorId || 'anon'}/${Date.now()}.webp`;

    // 2. Supabase Storage'a yüklemeyi dene
    const { error } = await supabase.storage
      .from('question-images')
      .upload(path, compressedBlob, { contentType: 'image/webp', upsert: false });

    if (error) {
      console.warn('Supabase Storage yükleme uyarısı (Fallback devreye giriyor):', error.message);
      throw error;
    }

    const { data } = supabase.storage.from('question-images').getPublicUrl(path);
    return data.publicUrl;
  } catch (err) {
    // 3. Bucket veya RLS yetki hatasında kullanıcının çalışmasını engelleme (Base64 Fallback)
    console.info('Görsel Base64 formatına çevrilerek editöre aktarılıyor...');
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          resolve('');
        }
      };
      reader.readAsDataURL(file);
    });
  }
}

// ─────────────────────────────────────────────────────────
// TESTS
// ─────────────────────────────────────────────────────────

/** Öğretmenin testlerini listele */
export async function fetchMyTests(authorId: string): Promise<Test[]> {
  const { data, error } = await supabase
    .from('tests')
    .select('*')
    .eq('author_id', authorId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Test[];
}

/** Kamuya / Öğrencilere açık genel testleri listele */
export async function fetchPublicTests(filters?: { lesson?: string; gradeLevel?: string }): Promise<Test[]> {
  let query = supabase
    .from('tests')
    .select('*')
    .eq('status', 'active')
    .eq('is_public', true)
    .not('share_code', 'is', null)
    .order('created_at', { ascending: false });

  if (filters?.lesson && filters.lesson !== 'Tümü') {
    query = query.eq('lesson', filters.lesson);
  }
  if (filters?.gradeLevel && filters.gradeLevel !== 'Tümü') {
    query = query.eq('grade_level', filters.gradeLevel);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Test[];
}

/** Tekil test getir */
export async function fetchTest(id: string): Promise<Test> {
  const { data, error } = await supabase
    .from('tests')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as Test;
}

/** Share code ile test getir (öğrenci çözümü için) */
export async function fetchTestByShareCode(shareCode: string): Promise<Test> {
  const { data, error } = await supabase
    .from('tests')
    .select('*')
    .eq('share_code', shareCode)
    .eq('status', 'active')
    .single();
  if (error) throw error;
  return data as Test;
}

/** Yeni test oluştur */
export async function createTest(
  draft: TestDraft,
  authorId: string
): Promise<Test> {
  const { data, error } = await supabase
    .from('tests')
    .insert({ ...draft, author_id: authorId })
    .select()
    .single();
  if (error) throw error;
  return data as Test;
}

/**
 * Test güncelle.
 * Kilitli (`is_locked`) bir testte soru listesi (`question_ids`) değiştirilemez —
 * kilit gerçek bir kısıtlama, sadece UI süsü değil.
 */
export async function updateTest(
  id: string,
  updates: Partial<TestDraft>
): Promise<Test> {
  if (updates.question_ids !== undefined && updates.is_locked === undefined) {
    const { data: current, error: checkError } = await supabase
      .from('tests')
      .select('is_locked')
      .eq('id', id)
      .single();
    if (checkError) throw checkError;
    if (current?.is_locked) {
      throw new Error(
        'Bu test kilitli olduğu için soru listesi değiştirilemez. Önce kilidi açın.'
      );
    }
  }

  const { data, error } = await supabase
    .from('tests')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Test;
}

/** Test sil */
export async function deleteTest(id: string): Promise<void> {
  const { error } = await supabase.from('tests').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Paylaşım kodu oluştur ve testi aktifleştir.
 * Ön koşul: test en az 1 soru içermeli. Daha önce yayınlanmışsa mevcut `share_code` korunur
 * (öğrenciye/videoya verilen link kalıcı olsun diye).
 */
export async function publishTest(id: string, isPublic?: boolean): Promise<string> {
  const { data: current, error: fetchError } = await supabase
    .from('tests')
    .select('question_ids, share_code')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  if (!current?.question_ids || current.question_ids.length === 0) {
    throw new Error('Test yayına alınamaz: Teste en az 1 soru eklemelisiniz.');
  }

  // 6 haneli büyük harf + rakam kod (yalnızca ilk yayında üretilir)
  const code: string = current.share_code || Math.random().toString(36).slice(2, 8).toUpperCase();
  const updateData: any = { share_code: code, status: 'active', updated_at: new Date().toISOString() };
  if (isPublic !== undefined) {
    updateData.is_public = isPublic;
  }
  const { error } = await supabase
    .from('tests')
    .update(updateData)
    .eq('id', id);
  if (error) throw error;
  return code;
}

/**
 * Testi yayından kaldır — durumu `draft`'a çeker.
 * `share_code` KORUNUR: tekrar yayına alındığında öğrenci/video linki aynı kalır.
 */
export async function unpublishTest(id: string): Promise<void> {
  const { error } = await supabase
    .from('tests')
    .update({ status: 'draft', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Test içeriğini dondur / çöz.
 * Kilitliyken soru listesi güncellenemez (bkz. `updateTest`).
 */
export async function setTestLocked(id: string, locked: boolean): Promise<void> {
  const { error } = await supabase
    .from('tests')
    .update({
      is_locked: locked,
      locked_at: locked ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────
// TEST RESULTS
// ─────────────────────────────────────────────────────────

/** Öğrenci çözüm sonucu kaydet */
export async function saveTestResult(
  testId: string,
  studentId: string,
  answers: TestAnswer[],
  totalTimeMs: number
): Promise<TestResult> {
  const correct = answers.filter((a) => a.is_correct).length;
  const score = answers.length > 0 ? (correct / answers.length) * 100 : 0;

  const { data, error } = await supabase
    .from('test_results')
    .insert({
      test_id: testId,
      student_id: studentId,
      answers,
      score: Math.round(score * 10) / 10,
      duration_seconds: Math.round(totalTimeMs / 1000),
    })
    .select()
    .single();
  if (error) throw error;
  return data as TestResult;
}

/** Öğrencinin kendi sonuçlarını getir */
export async function fetchMyResults(studentId: string): Promise<TestResult[]> {
  const { data, error } = await supabase
    .from('test_results')
    .select('*, tests(title, lesson)')
    .eq('student_id', studentId)
    .order('completed_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TestResult[];
}

/** Tekil sonuç getir — yanlış analizi / detay inceleme ekranı için */
export async function fetchTestResult(resultId: string): Promise<TestResult> {
  const { data, error } = await supabase
    .from('test_results')
    .select('*, tests(title, lesson)')
    .eq('id', resultId)
    .single();
  if (error) throw error;
  return data as TestResult;
}

export interface AuthorQuestionStat {
  question: Question;
  attemptCount: number;
  correctCount: number;
  successRate: number; // 0-100, deneme yoksa 0
  avgTimeMs: number;
}

/**
 * Bir öğretmenin yazdığı tüm sorular için deneme sayısı / doğru oranı / ortalama süre
 * istatistiklerini canlı hesaplar. `question_analytics` tablosu şu an hiçbir yerde
 * yazılmadığı için (bkz. analyticsEngine.ts), computeTestPsychometrics'teki gibi
 * `test_results.answers` verisi client tarafında agregre edilir.
 */
export async function fetchQuestionStatsForAuthor(authorId: string): Promise<AuthorQuestionStat[]> {
  const { data: questions, error: qError } = await supabase
    .from('questions')
    .select('*')
    .eq('author_id', authorId)
    .neq('is_deleted', true)
    .order('created_at', { ascending: false });
  if (qError) throw qError;

  const authorQuestions = (questions ?? []) as Question[];
  if (authorQuestions.length === 0) return [];

  const questionIds = new Set(authorQuestions.map((q) => q.id));

  const { data: resultRows, error: rError } = await supabase
    .from('test_results')
    .select('answers');
  if (rError) throw rError;

  const stats = new Map<string, { attempts: number; correct: number; timeSum: number }>();
  for (const row of (resultRows ?? []) as { answers: TestAnswer[] }[]) {
    for (const answer of row.answers ?? []) {
      if (!questionIds.has(answer.question_id)) continue;
      const s = stats.get(answer.question_id) ?? { attempts: 0, correct: 0, timeSum: 0 };
      s.attempts += 1;
      if (answer.is_correct) s.correct += 1;
      s.timeSum += answer.time_ms || 0;
      stats.set(answer.question_id, s);
    }
  }

  return authorQuestions
    .map((question) => {
      const s = stats.get(question.id);
      const attemptCount = s?.attempts ?? 0;
      const correctCount = s?.correct ?? 0;
      return {
        question,
        attemptCount,
        correctCount,
        successRate: attemptCount > 0 ? Math.round((correctCount / attemptCount) * 1000) / 10 : 0,
        avgTimeMs: attemptCount > 0 ? Math.round((s?.timeSum ?? 0) / attemptCount) : 0,
      };
    })
    .sort((a, b) => b.attemptCount - a.attemptCount);
}

export interface SolvedQuestionEntry {
  resultId: string;
  testTitle: string;
  lesson?: string;
  completedAt: string;
  questionId: string;
  content: string;
  isCorrect: boolean;
  selected: string | boolean | null;
  timeMs: number;
}

/** Öğrencinin bugüne kadar çözdüğü TÜM soruları (test bazlı değil, soru bazlı) tek liste halinde döner. */
export async function fetchSolvedQuestionsForStudent(studentId: string): Promise<SolvedQuestionEntry[]> {
  const results = await fetchMyResults(studentId);
  const entries: SolvedQuestionEntry[] = [];
  for (const r of results) {
    for (const a of r.answers ?? []) {
      entries.push({
        resultId: r.id,
        testTitle: r.tests?.title || 'Silinmiş Test',
        lesson: r.tests?.lesson,
        completedAt: r.completed_at,
        questionId: a.question_id,
        content: a.question_snapshot?.content ?? '',
        isCorrect: a.is_correct,
        selected: a.selected,
        timeMs: a.time_ms,
      });
    }
  }
  return entries;
}

export interface UserProfile {
  id: string; // Not: gerçek DB kolonu `user_id`dir (bkz. supabase_schema.sql notu) — burada `id` olarak eşleniyor.
  email?: string;
  full_name?: string;
  avatar_url?: string;
  role: string; // Gerçek DB varsayılanı 'user'; soru tarafı ayrıca 'educator'/'seller'/'admin' değerlerini de tanır.
  is_banned?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** profiles tablosundaki satırı UserProfile'a çevirir (gerçek kolon adı user_id -> id). */
function mapProfileRow(row: any): UserProfile {
  return {
    id: row.user_id,
    email: row.email,
    full_name: row.full_name,
    avatar_url: row.avatar_url,
    role: row.role,
    is_banned: Boolean(row.is_banned),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Supabase profiles tablosundan kullanıcı profilini çek */
export async function fetchUserProfile(userId: string): Promise<UserProfile | null> {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error || !data) {
    return null;
  }
  return mapProfileRow(data);
}

// ─────────────────────────────────────────────────────────
// BOOKS (Kitaplık — birden fazla testi tek A4 dokümanında birleştirir)
// ─────────────────────────────────────────────────────────

/** Öğretmenin kitaplarını listele */
export async function fetchMyBooks(authorId: string): Promise<Book[]> {
  const { data, error } = await supabase
    .from('books')
    .select('*')
    .eq('author_id', authorId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Book[];
}

/** Tekil kitap getir */
export async function fetchBook(id: string): Promise<Book> {
  const { data, error } = await supabase.from('books').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Book;
}

/** Yeni kitap oluştur */
export async function createBook(draft: BookDraft, authorId: string): Promise<Book> {
  const { data, error } = await supabase
    .from('books')
    .insert({ ...draft, author_id: authorId })
    .select()
    .single();
  if (error) throw error;
  return data as Book;
}

/** Kitap güncelle */
export async function updateBook(id: string, updates: Partial<BookDraft>): Promise<Book> {
  const { data, error } = await supabase
    .from('books')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Book;
}

/** Kitap sil */
export async function deleteBook(id: string): Promise<void> {
  const { error } = await supabase.from('books').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────
// SORU HAVUZLARI (Bayraklı / İşaretli Sorular)
// ─────────────────────────────────────────────────────────

/** Öğrencinin bayrakladığı tüm soru ID'lerini getirir (Set olarak — hızlı lookup için) */
export async function fetchFlaggedQuestionIds(studentId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('question_flags')
    .select('question_id')
    .eq('student_id', studentId);
  if (error) throw error;
  return new Set((data ?? []).map((row) => row.question_id as string));
}

/** Öğrencinin bayrakladığı soruların tam içeriğini getirir */
export async function fetchFlaggedQuestions(studentId: string): Promise<Question[]> {
  const ids = await fetchFlaggedQuestionIds(studentId);
  if (ids.size === 0) return [];
  return fetchQuestionsByIds(Array.from(ids));
}

/** Bir soruyu bayrakla / bayrağı kaldır. Yeni durumu (true = bayraklı) döner. */
export async function toggleQuestionFlag(studentId: string, questionId: string): Promise<boolean> {
  const { data: existing, error: findError } = await supabase
    .from('question_flags')
    .select('id')
    .eq('student_id', studentId)
    .eq('question_id', questionId)
    .maybeSingle();
  if (findError) throw findError;

  if (existing) {
    const { error } = await supabase.from('question_flags').delete().eq('id', existing.id);
    if (error) throw error;
    return false;
  }

  const { error } = await supabase
    .from('question_flags')
    .insert({ student_id: studentId, question_id: questionId });
  if (error) throw error;
  return true;
}

// ─────────────────────────────────────────────────────────
// PRATİK OTURUMU (Eksiklerini Kapat / İşaretlileri Çöz)
// ─────────────────────────────────────────────────────────

/**
 * Öğrencinin kendi kendine tekrar çözmesi için, verilen soru ID listesinden
 * özel (yayın olmayan) bir test oluşturur ve hemen yayına alır (share_code üretir).
 * Bu, mevcut /coz/:shareCode akışını (StepByStep/ScrollView/saveTestResult) hiç
 * değiştirmeden yeniden kullanmamızı sağlar — yeni bir "pratik oturumu" tablosu gerekmez.
 */
export async function createPracticeSession(
  studentId: string,
  title: string,
  lesson: string | undefined,
  questionIds: string[]
): Promise<string> {
  if (questionIds.length === 0) {
    throw new Error('Pratik oturumu oluşturulamadı: soru listesi boş.');
  }
  const test = await createTest(
    {
      title,
      lesson,
      question_ids: questionIds,
      settings: { ...DEFAULT_TEST_SETTINGS },
      status: 'draft',
      is_public: false,
    } as TestDraft,
    studentId
  );
  const shareCode = await publishTest(test.id, false);
  return shareCode;
}

